/**
 * Ellipse bridge helpers — DXF ELLIPSE <-> drawing-model EllipseEntity.
 *
 * Lives in its own module to keep dxfBridge.ts under the 2000-line
 * ceiling (CLAUDE.md: ">2000 не допускать; разбивать в рамках той же
 * задачи"). Mirrors the splitting pattern established by
 * `dxfBridge.blockRecords.ts`.
 *
 * Public surface:
 *   - drawingEllipseToDxfEllipse(entity, common): DxfEllipseEntity
 *   - dxfEllipseToDrawingEllipse(dxf, base): EllipseEntity
 *
 * dxfBridge.ts imports both helpers and dispatches them inside its
 * top-level `toDxfEntity` / `toDrawingEntity` switches.
 */
import type { DxfEllipseEntity } from "@dxf-ai-editor/dxf-io";

import type {
  DrawingEntityBase,
  DrawingPoint3D,
  EllipseEntity,
} from "./model";

const DEFAULT_EXTRUSION: DrawingPoint3D = { x: 0, y: 0, z: 1 };

function isDefaultExtrusion(p: DrawingPoint3D | undefined): boolean {
  if (!p) return true;
  return p.x === 0 && p.y === 0 && p.z === 1;
}

/**
 * Convert a drawing-model EllipseEntity into the DXF ELLIPSE wire
 * shape. The OCS extrusion vector is written into common.extrusion by
 * the caller (toDxfCommonProps does not yet override it; we set it on
 * `common` before passing to the writer when the user-authored ellipse
 * has a non-default extrusion).
 */
export function drawingEllipseToDxfEllipse(
  entity: EllipseEntity,
  common: DxfEllipseEntity["common"],
  base: { kind: "supported-entity"; subclassMarkers: string[]; extraGroups: { code: number; value: string }[] },
): DxfEllipseEntity {
  const finalCommon = { ...common };
  if (!isDefaultExtrusion(entity.extrusion)) {
    finalCommon.extrusion = {
      x: entity.extrusion.x,
      y: entity.extrusion.y,
      z: entity.extrusion.z,
    };
  }
  if (entity.thickness !== 0) {
    finalCommon.thickness = entity.thickness;
  }
  return {
    kind: base.kind,
    common: finalCommon,
    subclassMarkers: base.subclassMarkers,
    extraGroups: base.extraGroups,
    type: "ELLIPSE",
    geometry: {
      // SL-15 — carry the model center-z (DXF 30) and major-axis-z (31). The
      // fields are optional-z (drawingPointOptionalZSchema); emit `z ?? 0` so a
      // planar ellipse writes `30 0`/`31 0` (byte-identical to before + to
      // ezdxf, which always emits a Vec3) while an elevated / tilted ellipse
      // survives the round-trip instead of being flattened to XY.
      center: { x: entity.center.x, y: entity.center.y, z: entity.center.z ?? 0 },
      majorAxisEndpoint: {
        x: entity.majorAxisEndpoint.x,
        y: entity.majorAxisEndpoint.y,
        z: entity.majorAxisEndpoint.z ?? 0,
      },
      ratio: entity.ratio,
      startParam: entity.startParam,
      endParam: entity.endParam,
    },
  };
}

/**
 * Convert a DXF ELLIPSE entity into a drawing-model EllipseEntity.
 * Accepts the pre-built DrawingEntityBase the caller has already
 * computed from the DXF common props (handle, layer, color, etc.).
 */
export function dxfEllipseToDrawingEllipse(
  dxf: DxfEllipseEntity,
  base: DrawingEntityBase,
): EllipseEntity {
  const g = dxf.geometry;
  return {
    ...base,
    type: "Ellipse",
    // SL-15 round-6 @codex F2 — the dxf-io parser ALWAYS supplies a z for
    // groups 30/31 (`readPoint` defaults `z ?? 0`), so a PLANAR ellipse hands
    // in z=0. Storing that 0 as a PRESENT optional-z field makes
    // `deriveEllipsePreviewProperties` emit spurious "Center Z"/"Major axis Z"
    // rows and breaks the planar byte-identical invariant. OMIT z when the
    // source is 0 (planar); keep it ONLY when genuinely non-zero (a true 3D /
    // tilted ellipse). Export re-emits `z ?? 0` → 30/31 round-trip unchanged
    // (the writer always writes the 3D point, ezdxf-parity).
    center:
      g.center.z !== 0
        ? { x: g.center.x, y: g.center.y, z: g.center.z }
        : { x: g.center.x, y: g.center.y },
    majorAxisEndpoint:
      g.majorAxisEndpoint.z !== 0
        ? {
            x: g.majorAxisEndpoint.x,
            y: g.majorAxisEndpoint.y,
            z: g.majorAxisEndpoint.z,
          }
        : { x: g.majorAxisEndpoint.x, y: g.majorAxisEndpoint.y },
    ratio: g.ratio,
    startParam: g.startParam,
    endParam: g.endParam,
    extrusion: dxf.common.extrusion
      ? { x: dxf.common.extrusion.x, y: dxf.common.extrusion.y, z: dxf.common.extrusion.z }
      : { ...DEFAULT_EXTRUSION },
    thickness: dxf.common.thickness ?? 0,
  };
}
